import { AlertCircle, CalendarX2, CheckCircle2, Edit2, SlidersHorizontal } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { GlobalRoleAssignment, FirmRoleAssignmentWithWeight, TeamMemberPayload } from '@/services/api/firmsService';
import {
  useGetGlobalTeamDistributionQuery,
  useSetGlobalTeamDistributionMutation,
} from '@/services/api/teamDistributionService';
import { cn } from '@/lib/utils';
import { EditTeamDialog } from '@/components/firms/task-distribution/EditTeamDialog';

// ─── Role config ─────────────────────────────────────────────────────────────

const WEIGHTED_ROLES = [
  { role: 'coverage_team', label: 'Coverage Team', description: 'Handles case coverage assignments' },
  { role: 'draft_demand_team', label: 'Draft Demand Team', description: 'Drafts demand letters for cases' },
  { role: 'foia_manager', label: 'FOIA Team', description: 'Manages Freedom of Information Act requests' },
  { role: 'liens_team', label: 'Liens Team', description: 'Manages medical lien resolution' },
  { role: 'pip_medpay_manager', label: 'PIP / MedPay Team', description: 'Handles PIP and MedPay applications' },
  { role: 'police_report_manager', label: 'Police Report Team', description: 'Obtains and processes police reports' },
] as const;

const MEMBER_COLORS = [
  'bg-violet-500', 'bg-sky-500', 'bg-emerald-500',
  'bg-amber-500', 'bg-rose-500', 'bg-indigo-500',
];
const getMemberColor = (i: number) => MEMBER_COLORS[i % MEMBER_COLORS.length];

// Adapt GlobalRoleAssignment → FirmRoleAssignmentWithWeight for EditTeamDialog
function toFirmAssignment(g: GlobalRoleAssignment): FirmRoleAssignmentWithWeight {
  return {
    id: g.id,
    firm_id: '',
    role: g.role as FirmRoleAssignmentWithWeight['role'],
    assignee_id: g.assignee_id,
    assignee: g.assignee,
    config: g.config,
    created_at: '',
    modified_at: '',
  } as FirmRoleAssignmentWithWeight;
}

// ─── Availability badge ──────────────────────────────────────────────────────

const AvailabilityBadge: React.FC<{ assignment: GlobalRoleAssignment }> = ({ assignment }) => {
  const cfg = assignment.config;
  const today = new Date().toISOString().split('T')[0];

  if (cfg?.weight === 0) {
    return (
      <Badge variant="outline" className="text-xs text-zinc-500 border-zinc-200 bg-zinc-50">
        Paused
      </Badge>
    );
  }
  if (cfg?.available_from && today < cfg.available_from) {
    return (
      <Badge variant="outline" className="text-xs text-amber-700 border-amber-200 bg-amber-50">
        <CalendarX2 className="h-3 w-3 mr-1" />
        From {cfg.available_from}
      </Badge>
    );
  }
  if (cfg?.available_until && today > cfg.available_until) {
    return (
      <Badge variant="outline" className="text-xs text-red-600 border-red-200 bg-red-50">
        <AlertCircle className="h-3 w-3 mr-1" />
        Expired
      </Badge>
    );
  }
  if (cfg?.available_until) {
    return (
      <Badge variant="outline" className="text-xs text-amber-700 border-amber-200 bg-amber-50">
        <CalendarX2 className="h-3 w-3 mr-1" />
        Until {cfg.available_until}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-200 bg-emerald-50">
      <CheckCircle2 className="h-3 w-3 mr-1" />
      Active
    </Badge>
  );
};

// ─── Weighted team card ──────────────────────────────────────────────────────

const WeightedTeamCard: React.FC<{
  label: string;
  description: string;
  assignments: GlobalRoleAssignment[];
  onEdit: () => void;
}> = ({ label, description, assignments, onEdit }) => {
  const totalWeight = assignments.reduce(
    (sum, a) => sum + Math.max(0, (a.config?.weight ?? 1) * 100),
    0,
  );
  const segments = assignments
    .map((a, i) => ({
      label: a.assignee.display_name,
      pct: totalWeight > 0 ? (Math.max(0, (a.config?.weight ?? 1) * 100) / totalWeight) * 100 : 0,
      color: getMemberColor(i),
    }))
    .filter((s) => s.pct > 0);

  return (
    <div className="border border-zinc-200 rounded-xl p-5 space-y-4 bg-white hover:border-zinc-300 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">{label}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">{description}</p>
        </div>
        <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs gap-1.5 shrink-0" onClick={onEdit}>
          <Edit2 className="h-3 w-3" />
          Edit
        </Button>
      </div>

      {assignments.length === 0 ? (
        <div className="text-xs text-zinc-400 italic border border-dashed border-zinc-200 rounded-lg py-3 text-center">
          Using system defaults
        </div>
      ) : (
        <>
          <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-zinc-100 gap-px">
            {segments.length > 0
              ? segments.map((seg, i) => (
                  <div
                    key={i}
                    className={cn('transition-all duration-300', seg.color)}
                    style={{ width: `${seg.pct}%` }}
                    title={`${seg.label}: ${seg.pct.toFixed(0)}%`}
                  />
                ))
              : <div className="w-full h-full bg-zinc-200 rounded-full" />}
          </div>
          <div className="space-y-2">
            {assignments.map((a, i) => {
              const pct =
                totalWeight > 0
                  ? ((Math.max(0, (a.config?.weight ?? 1) * 100) / totalWeight) * 100).toFixed(0)
                  : '0';
              return (
                <div key={a.id} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={cn('w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-semibold shrink-0', getMemberColor(i))}>
                      {a.assignee.display_name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm text-zinc-700 truncate">{a.assignee.display_name}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-medium text-zinc-500 tabular-nums w-8 text-right">{pct}%</span>
                    <AvailabilityBadge assignment={a} />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const TaskDistributionPage: React.FC = () => {
  const { data: assignments = [], isLoading, refetch } = useGetGlobalTeamDistributionQuery();
  const [setGlobalDistribution] = useSetGlobalTeamDistributionMutation();
  const [editingRole, setEditingRole] = useState<{ role: string; label: string } | null>(null);

  const assignmentsByRole = useMemo(
    () =>
      assignments.reduce((map, assignment) => {
        map.set(assignment.role, [...(map.get(assignment.role) ?? []), assignment]);
        return map;
      }, new Map<string, GlobalRoleAssignment[]>()),
    [assignments],
  );

  const editingAssignments = useMemo(
    () => (editingRole ? (assignmentsByRole.get(editingRole.role) ?? []) : []),
    [editingRole, assignmentsByRole],
  );

  const handleSaveMembers = async (members: TeamMemberPayload[]) => {
    if (!editingRole) return;
    await setGlobalDistribution({ role: editingRole.role, members }).unwrap();
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-6 space-y-8">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-zinc-100">
          <SlidersHorizontal className="h-5 w-5 text-zinc-600" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Task Distribution</h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            Configure global assignment weights for Finch operations teams. Changes apply across all firms.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isLoading
          ? WEIGHTED_ROLES.map((r) => (
              <div key={r.role} className="border border-zinc-200 rounded-xl p-5 space-y-3 bg-white">
                <div className="flex items-start justify-between">
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="h-3 w-52" />
                  </div>
                  <Skeleton className="h-7 w-16" />
                </div>
                <Skeleton className="h-2.5 w-full rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-3/4" />
                </div>
              </div>
            ))
          : WEIGHTED_ROLES.map((config) => (
              <WeightedTeamCard
                key={config.role}
                label={config.label}
                description={config.description}
                assignments={assignmentsByRole.get(config.role) ?? []}
                onEdit={() => setEditingRole({ role: config.role, label: config.label })}
              />
            ))}
      </div>

      {editingRole && (
        <EditTeamDialog
          firmId=""
          role={editingRole.role}
          roleLabel={editingRole.label}
          currentAssignments={editingAssignments.map(toFirmAssignment)}
          open={!!editingRole}
          onOpenChange={(open) => { if (!open) setEditingRole(null); }}
          onSaved={() => refetch()}
          onSaveMembers={handleSaveMembers}
        />
      )}
    </div>
  );
};
